import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import PublicHeader from '../components/PublicHeader';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import Footer from '../components/Footer';
import { 
  Plus,
  Trash2,
  Download,
  Loader2,
  Image as ImageIcon,
  X
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { SITE_URL, faqJsonLd, Seo } from '../components/Seo';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const HOWTO_JSON_LD = {
  '@context': 'https://schema.org',
  '@type': 'HowTo',
  name: 'How to create a free PDF invoice',
  description: 'Create and download a professional PDF invoice in about a minute, free and without an account.',
  step: [
    { '@type': 'HowToStep', name: 'Enter your details', text: 'Add your name or business name, your client\'s details, and an invoice number.' },
    { '@type': 'HowToStep', name: 'Add line items', text: 'List what you\'re billing for with quantities and rates — tax and totals calculate automatically.' },
    { '@type': 'HowToStep', name: 'Download the PDF', text: 'Download your finished invoice as a PDF and send it to your client.' },
  ],
};

// SEO data for different niche pages
const SEO_DATA = {
  '/free-invoice-generator': {
    title: 'Free Invoice Generator — Download a PDF Invoice in 60 Seconds',
    h1: 'Free Invoice Generator',
    description: 'Create and download a professional PDF invoice in 60 seconds. No signup, no account needed — completely free.',
    intro: null
  },
  '/invoice-template/photographers': {
    title: 'Free Photography Invoice Template & Generator (PDF)',
    h1: 'Free Invoice Template for Photographers',
    description: 'Create a professional photography invoice in about a minute. Sessions, prints, licensing, editing — download as PDF, no signup required.',
    intro: 'Create a professional photography invoice in about a minute — sessions, prints, licensing, editing hours, whatever you bill for. Fill it in, download the PDF, send it to your client. No account, no watermark on your details, completely free.',
    nicheLabel: 'photography',
    lineItems: [
      { name: 'Session fee', note: 'The shoot itself — a flat rate for the booking, or an hourly/half-day/full-day rate. This is usually the biggest line, so describe what it covers: date, location, and hours on site.' },
      { name: 'Editing and retouching', note: 'Culling, color correction, and retouching are real hours. Bill them hourly or per finished image, and say how many delivered images the price includes.' },
      { name: 'Prints and albums', note: 'Physical products carry their own costs — list each print size, album, or wall art piece separately with quantity and unit price.' },
      { name: 'Image licensing', note: 'If a business is using your photos, the usage license is its own line. Describe the scope: where the images can run, for how long, and whether it is exclusive.' },
      { name: 'Travel and expenses', note: 'Mileage, parking, permits, or an overnight stay for a destination shoot. Clients accept these far more readily when they are itemized instead of buried in the session fee.' }
    ],
    terms: 'Most photographers take a retainer when the date is booked — commonly 25–50% — with the balance due on delivery of the final gallery. For weddings and events, full payment before the event date is standard practice, since there is no product to withhold afterwards. Portrait and commercial work is usually invoiced on delivery with net 14 terms; commercial clients may push for net 30, which is fine if you have priced for the wait. If a client is repeatedly slow, a stated late fee on the invoice — flat or a monthly percentage — changes behavior more than reminder emails do.',
    faqs: [
      {
        q: 'What should a photography invoice include?',
        a: 'Your business name and contact info, the client\'s details, a unique invoice number, the shoot date and services (session fee, editing, prints, image licensing), rates, any tax, the total, and your payment terms with a due date.'
      },
      {
        q: 'Is this really free?',
        a: 'Yes — make and download as many PDF invoices as you like. If you want to email invoices, accept card payments, or send automatic reminders, that\'s what CreateCollect accounts are for (the free plan includes 2 full invoices a month).'
      },
      {
        q: 'Can clients pay this invoice online?',
        a: 'A downloaded PDF is just a document — clients pay you however you arrange. With a free CreateCollect account, your invoices come with a payment link so clients can pay by card and you get notified instantly.'
      },
      {
        q: 'Should I charge a deposit for photography work?',
        a: 'Most photographers ask for a retainer of 25–50% to hold the date, with the balance due on delivery of the final gallery. Put the retainer on its own invoice or list it as the first line item, and state clearly whether it is refundable.'
      },
      {
        q: 'How do I bill for image licensing?',
        a: 'List the license as its own line item and describe the scope — for example "Commercial use, web and social, 2 years". Licensing is separate from your session fee, so pricing it separately makes renewals and extended usage easier to charge for later.'
      }
    ]
  },
  '/invoice-template/musicians': {
    title: 'Free Musician Invoice Template & Generator (PDF)',
    h1: 'Free Invoice Template for Musicians & Producers',
    description: 'Create a professional music invoice in about a minute. Session fees, mixing, mastering, production — download as PDF, no signup required.',
    intro: 'Create a professional music invoice in about a minute — session fees, mixing, mastering, production work, licensing. Fill it in, download the PDF, send it to your client. No account, no watermark on your details, completely free.',
    nicheLabel: 'music',
    lineItems: [
      { name: 'Tracking day rate', note: 'Recording sessions are usually billed per day or half-day. Note the date and what was tracked so the line makes sense months later.' },
      { name: 'Mixing, per song', note: 'Mixing is most often priced per song. Say how many revision passes the price includes — unlimited revisions is how mixes never get finished.' },
      { name: 'Mastering', note: 'Per song or per EP/album. If you deliver multiple formats (streaming masters, CD, vinyl pre-master), list them so the client sees what they got.' },
      { name: 'Session player fees', note: 'If you hired players, either pass their fees through as separate lines or fold them into your rate — but pick one and be consistent, because clients will ask.' },
      { name: 'Studio time', note: 'When you book a room, the studio cost is its own line item at cost or with your markup. Keeping it separate from your creative fee protects both numbers.' },
      { name: 'Publishing note', note: 'Points and splits do not belong on an invoice — they live in a split sheet or agreement. If you have one, reference it in the invoice notes rather than billing royalties here.' }
    ],
    terms: 'For project work like production, mixing, or mastering, a 50% deposit before work starts is the norm, with the balance due before final files are delivered — deliver unwatermarked masters only after payment clears. Session and live work is usually paid same-day or net 7, since there is nothing to withhold. Labels and larger clients often run net 30 through accounts payable; get a purchase order or written confirmation before the session so the invoice does not stall. Whatever the arrangement, put the due date on the invoice — "due on receipt" without a date is the easiest term for a client to ignore.',
    faqs: [
      {
        q: 'What should a musician invoice include?',
        a: 'Your name or studio name and contact info, the client\'s details, an invoice number, session dates and services (recording, mixing, mastering, production, sync licensing), rates, any tax, the total, and payment terms with a due date.'
      },
      {
        q: 'Is this really free?',
        a: 'Yes — make and download as many PDF invoices as you like. If you want to email invoices, accept card payments, or send automatic reminders, that\'s what CreateCollect accounts are for (the free plan includes 2 full invoices a month).'
      },
      {
        q: 'Can clients pay this invoice online?',
        a: 'A downloaded PDF is just a document — clients pay you however you arrange. With a free CreateCollect account, your invoices come with a payment link so clients can pay by card and you get notified instantly.'
      },
      {
        q: 'Should I invoice per song or per hour?',
        a: 'Both are common. Mixing and mastering are usually priced per song, while session work and studio time are billed by the hour or day. Whichever you choose, spell it out on the line item so the client knows exactly what the rate covers.'
      },
      {
        q: 'Do I need to mention publishing splits on an invoice?',
        a: 'No — splits belong in a split sheet or agreement, not an invoice. An invoice covers fees for services like production, mixing, or session playing. If you have agreed points or royalties, reference the separate agreement in the notes instead of trying to bill them here.'
      }
    ]
  },
  '/invoice-template/designers': {
    title: 'Free Graphic Design Invoice Template & Generator (PDF)',
    h1: 'Free Invoice Template for Designers',
    description: 'Create a professional design invoice in about a minute. Logo design, branding, hourly work — download as PDF, no signup required.',
    intro: 'Create a professional design invoice in about a minute — logo design, branding packages, hourly design work, revisions. Fill it in, download the PDF, send it to your client. No account, no watermark on your details, completely free.',
    nicheLabel: 'design',
    lineItems: [
      { name: 'Discovery and research', note: 'The kickoff work — brief, competitor review, moodboards. Billing it separately shows the client that strategy is work, not a freebie before "the real design".' },
      { name: 'Concepting', note: 'Initial directions or concept rounds. Note how many concepts the price includes; extra directions beyond that become a new line.' },
      { name: 'Design hours', note: 'Production design billed hourly — list the hours and rate so the math is visible on the invoice.' },
      { name: 'Revision rounds', note: 'Include the agreed number of rounds in the project fee, and bill additional rounds as their own line item. This is the single best way to keep scope honest.' },
      { name: 'File handoff', note: 'Final packaged files — vector sources, exports for web and print, font licenses noted. A named handoff line marks the project as delivered.' },
      { name: 'Brand guidelines', note: 'If you produce a guidelines document, it is a deliverable in its own right and deserves its own price.' }
    ],
    terms: 'Design projects almost always start with a deposit — 50% up front is the standard for fixed-price work, with the balance due on delivery of final files. Withhold source files until the final payment clears; exports for review are fine, editable files are not. Hourly and retainer arrangements usually invoice at the end of each month on net 14. Put the included revision rounds in your terms as well as your proposal — the invoice is the document clients actually re-read when a "quick extra round" request comes in.',
    faqs: [
      {
        q: 'What should a design invoice include?',
        a: 'Your business name and contact info, the client\'s details, an invoice number, project description (logo design, brand identity, web design, hourly work), rates, any tax, the total, and payment terms with a due date.'
      },
      {
        q: 'Is this really free?',
        a: 'Yes — make and download as many PDF invoices as you like. If you want to email invoices, accept card payments, or send automatic reminders, that\'s what CreateCollect accounts are for (the free plan includes 2 full invoices a month).'
      },
      {
        q: 'Can clients pay this invoice online?',
        a: 'A downloaded PDF is just a document — clients pay you however you arrange. With a free CreateCollect account, your invoices come with a payment link so clients can pay by card and you get notified instantly.'
      },
      {
        q: 'How do I charge for revisions?',
        a: 'Include a set number of revision rounds in your project price — two is common — and list extra rounds as their own line item at an hourly rate. Saying this on the invoice and in your proposal keeps revision requests from eating the budget.'
      },
      {
        q: 'Should I bill hourly or per project for design work?',
        a: 'Project pricing works well for defined deliverables like a logo or brand identity, while hourly suits open-ended or ongoing work. Many designers mix both: a fixed fee for the core deliverable plus an hourly rate for extras and revisions beyond the agreed scope.'
      }
    ]
  },
  '/invoice-template/filmmakers': {
    title: 'Free Videography Invoice Template & Generator (PDF)',
    h1: 'Free Invoice Template for Filmmakers & Videographers',
    description: 'Create a professional video production invoice in about a minute. Day rates, editing, equipment — download as PDF, no signup required.',
    intro: 'Create a professional video production invoice in about a minute — day rates, editing hours, equipment rental, post-production. Fill it in, download the PDF, send it to your client. No account, no watermark on your details, completely free.',
    nicheLabel: 'video production',
    lineItems: [
      { name: 'Pre-production', note: 'Scripting, storyboards, location scouting, casting, scheduling. Billing pre-pro separately makes it clear the project started before anyone touched a camera.' },
      { name: 'Shoot day rate', note: 'Your rate per production day, with the date and location noted. Half-days and overtime hours get their own lines at their own rates.' },
      { name: 'Crew', note: 'Second shooter, sound, gaffer, assistant — pass through each crew fee as a line item so the client sees where the day rate budget goes.' },
      { name: 'Editing', note: 'Post is usually the longest phase. Bill hourly or per finished minute, and state how many review rounds the price includes.' },
      { name: 'Color and sound finishing', note: 'Color grading, mix, and any licensed music are finishing costs that deserve their own lines — especially music licensing, which the client should see itemized.' },
      { name: 'Delivery formats', note: 'Master file plus social cuts, vertical versions, or captioned exports. Each extra deliverable is a line, not a favor.' }
    ],
    terms: 'Production work is normally split into milestone payments: a deposit before pre-production begins (a third to a half), a payment on or before the shoot day, and the balance on delivery of the final cut. Never hand over the master file before the last payment clears — watermarked review copies exist for exactly this reason. Small single-day shoots can go on one invoice due on delivery with net 14. Commercial clients and agencies will often ask for net 30; agree to it in writing before the shoot, not after, and price the wait into the job.',
    faqs: [
      {
        q: 'What should a videography invoice include?',
        a: 'Your business name and contact info, the client\'s details, an invoice number, shoot dates and services (day rate, editing, color grading, equipment rental), rates, any tax, the total, and payment terms with a due date.'
      },
      {
        q: 'Is this really free?',
        a: 'Yes — make and download as many PDF invoices as you like. If you want to email invoices, accept card payments, or send automatic reminders, that\'s what CreateCollect accounts are for (the free plan includes 2 full invoices a month).'
      },
      {
        q: 'Can clients pay this invoice online?',
        a: 'A downloaded PDF is just a document — clients pay you however you arrange. With a free CreateCollect account, your invoices come with a payment link so clients can pay by card and you get notified instantly.'
      },
      {
        q: 'How should I invoice for a multi-day shoot?',
        a: 'List each shoot day as its own line item with the day rate, then separate lines for crew, equipment, and post-production. That way the client can see exactly where the budget went, and changes — like an added half-day — are easy to bill.'
      },
      {
        q: 'When should I invoice for a video project?',
        a: 'For most projects: a deposit before pre-production, a payment on the final shoot day, and the balance on delivery of the final cut. For small single-day shoots, one invoice on delivery is fine. Tie each payment to a concrete milestone the client can verify.'
      }
    ]
  },
  '/invoice-template/freelancers': {
    title: 'Free Freelance Invoice Template & Generator (PDF)',
    h1: 'Free Invoice Template for Freelancers',
    description: 'Create a professional freelance invoice in about a minute. Hourly work, project fees, retainers — download as PDF, no signup required.',
    intro: 'Create a professional freelance invoice in about a minute — hourly work, project fees, retainers, whatever you bill for. Fill it in, download the PDF, send it to your client. No account, no watermark on your details, completely free.',
    nicheLabel: 'freelance',
    lineItems: [
      { name: 'Hourly work', note: 'List the hours and the rate on each line so the total is verifiable at a glance. Group by task or week rather than one opaque "services" line.' },
      { name: 'Project rate', note: 'For fixed-price work, one line with the agreed fee and a clear description of the deliverable. If the scope changed mid-project, the change is a new line — not a silent edit to the old one.' },
      { name: 'Milestones', note: 'Longer projects bill better in stages: a line per completed milestone, invoiced as each one is delivered and approved.' },
      { name: 'Expenses', note: 'Software seats, stock assets, travel — anything you agreed to pass through, itemized with receipts available on request.' },
      { name: 'Rush fees', note: 'If the client compressed your timeline, a rush fee (often 25–50% of the affected work) is a legitimate line item — provided it was agreed before you started the sprint.' }
    ],
    terms: 'For new clients, ask for a deposit before starting — 30–50% is typical — and keep the balance on delivery. Established clients usually move to invoicing on completion or monthly, with net 14 as a sensible default; net 30 is common with larger companies, so decide up front whether the job is worth the wait. Invoice immediately when the work ships, not at the end of the month — every day between delivery and invoice is a day added to your payment time. State a late fee in your terms and put it on the invoice itself, because terms a client never sees are terms that do not exist.',
    faqs: [
      {
        q: 'What should a freelance invoice include?',
        a: 'Your name or business name and contact info, the client\'s details, an invoice number, description of work completed, hourly rates or project fees, any tax, the total, and payment terms with a due date.'
      },
      {
        q: 'Is this really free?',
        a: 'Yes — make and download as many PDF invoices as you like. If you want to email invoices, accept card payments, or send automatic reminders, that\'s what CreateCollect accounts are for (the free plan includes 2 full invoices a month).'
      },
      {
        q: 'Can clients pay this invoice online?',
        a: 'A downloaded PDF is just a document — clients pay you however you arrange. With a free CreateCollect account, your invoices come with a payment link so clients can pay by card and you get notified instantly.'
      },
      {
        q: 'Hourly or fixed price — what should the invoice show?',
        a: 'For hourly work, list hours and the rate per line so the math is visible. For fixed-price projects, one line with the agreed fee is enough — optionally broken into milestones. Avoid mixing the two on a single line; it makes the invoice hard to defend if questioned.'
      },
      {
        q: 'Can I add a late fee to a freelance invoice?',
        a: 'Yes, if your terms said so up front. A common approach is a flat fee or 1.5% per month after the due date. State it in the payment terms on the invoice — "Net 14. 1.5% monthly late fee applies after the due date" — so it is agreed in writing and visible.'
      }
    ]
  }
};

const CURRENCIES = [
  { code: 'USD', symbol: '$', name: 'US Dollar' },
  { code: 'EUR', symbol: '€', name: 'Euro' },
  { code: 'GBP', symbol: '£', name: 'British Pound' },
  { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar' },
  { code: 'AUD', symbol: 'A$', name: 'Australian Dollar' }
];

const NICHE_LINKS = [
  { path: '/invoice-template/photographers', label: 'Photographers' },
  { path: '/invoice-template/musicians', label: 'Musicians' },
  { path: '/invoice-template/designers', label: 'Designers' },
  { path: '/invoice-template/filmmakers', label: 'Filmmakers' },
  { path: '/invoice-template/freelancers', label: 'Freelancers' }
];

const FreeInvoiceGenerator = () => {
  const location = useLocation();
  const seoData = SEO_DATA[location.pathname] || SEO_DATA['/free-invoice-generator'];
  
  const [isLoading, setIsLoading] = useState(false);
  const [showConversionModal, setShowConversionModal] = useState(false);
  
  // Form state
  const [sender, setSender] = useState({
    name: '',
    email: '',
    phone: '',
    address: ''
  });
  
  const [client, setClient] = useState({
    name: '',
    email: '',
    address: ''
  });
  
  const [invoice, setInvoice] = useState({
    number: 'INV-001',
    date: new Date().toISOString().split('T')[0],
    due_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    currency: 'USD',
    tax_rate: 0,
    notes: ''
  });
  
  const [items, setItems] = useState([
    { id: crypto.randomUUID(), description: '', quantity: 1, rate: 0 }
  ]);
  
  const [logo, setLogo] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  
  // Live preview state
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  
  const buildPayload = () => ({
    sender: {
      name: sender.name,
      email: sender.email || null,
      phone: sender.phone || null,
      address: sender.address || null
    },
    client: {
      name: client.name,
      email: client.email || null,
      address: client.address || null
    },
    invoice: {
      number: invoice.number,
      date: invoice.date,
      due_date: invoice.due_date,
      currency: invoice.currency,
      tax_rate: invoice.tax_rate || null,
      notes: invoice.notes || null
    },
    items: items.filter(item => item.description.trim()).map(item => ({
      description: item.description,
      quantity: item.quantity,
      rate: item.rate
    })),
    logo_base64: logo || null
  });
  
  // Debounced live preview
  useEffect(() => {
    const canPreview = sender.name.trim() && client.name.trim() && items.some(i => i.description.trim() && i.rate > 0);
    if (!canPreview) return;
    const timer = setTimeout(async () => {
      setPreviewLoading(true);
      try {
        const response = await axios.post(`${API_URL}/api/free-invoice/preview`, buildPayload(), { responseType: 'blob' });
        const url = window.URL.createObjectURL(new Blob([response.data], { type: 'image/png' }));
        setPreviewUrl(prev => {
          if (prev) window.URL.revokeObjectURL(prev);
          return url;
        });
      } catch (error) {
        // Preview is best-effort; stay silent on rate limits/transient errors
      } finally {
        setPreviewLoading(false);
      }
    }, 900);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sender, client, invoice, items, logo]);
  
  // Calculate totals
  const subtotal = items.reduce((sum, item) => sum + (item.quantity * item.rate), 0);
  const taxAmount = subtotal * (invoice.tax_rate / 100);
  const total = subtotal + taxAmount;
  const currencySymbol = CURRENCIES.find(c => c.code === invoice.currency)?.symbol || '$';
  
  const addItem = () => {
    if (items.length >= 15) {
      toast.error('Maximum 15 items allowed');
      return;
    }
    setItems([...items, { id: crypto.randomUUID(), description: '', quantity: 1, rate: 0 }]);
  };
  
  const removeItem = (index) => {
    if (items.length === 1) return;
    setItems(items.filter((_, i) => i !== index));
  };
  
  const updateItem = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = field === 'description' ? value : parseFloat(value) || 0;
    setItems(newItems);
  };
  
  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (file.size > 1024 * 1024) {
      toast.error('Logo must be under 1MB');
      return;
    }
    
    const reader = new FileReader();
    reader.onload = (e) => {
      setLogo(e.target.result);
      setLogoPreview(e.target.result);
    };
    reader.readAsDataURL(file);
  };
  
  const removeLogo = () => {
    setLogo(null);
    setLogoPreview(null);
  };
  
  const handleDownload = async () => {
    // Validate required fields
    if (!sender.name.trim()) {
      toast.error('Please enter your name or business name');
      return;
    }
    if (!client.name.trim()) {
      toast.error('Please enter client name');
      return;
    }
    if (!items.some(item => item.description.trim() && item.rate > 0)) {
      toast.error('Please add at least one item with a description and rate');
      return;
    }
    
    setIsLoading(true);
    
    try {
      const response = await axios.post(
        `${API_URL}/api/free-invoice/pdf`,
        buildPayload(),
        { responseType: 'blob' }
      );
      
      // Download the PDF
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `invoice-${invoice.number.replace(/\s+/g, '-')}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Invoice downloaded!');
      
      // Show conversion modal
      setShowConversionModal(true);
      
    } catch (error) {
      if (error.response?.status === 429) {
        toast.error('Too many requests. Please wait a moment and try again.');
      } else if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Failed to generate invoice. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Seo
        title={seoData.title}
        description={seoData.description}
        path={location.pathname}
        jsonLd={seoData.faqs ? faqJsonLd(seoData.faqs) : HOWTO_JSON_LD}
      />
      
      {/* Header */}
      <PublicHeader secondaryLabel="About" secondaryHref="/about" />

      {/* Main Content */}
      <main className="flex-1 pt-24 pb-16 px-6">
        <div className="max-w-6xl mx-auto">
          {/* Hero */}
          <div className="text-center mb-8">
            <h1 className="text-3xl sm:text-4xl font-heading font-bold mb-4" data-testid="generator-h1">
              {seoData.h1}
            </h1>
            {seoData.intro && (
              <p className="text-muted-foreground max-w-2xl mx-auto mb-4">
                {seoData.intro}
              </p>
            )}
            <p className="text-sm text-muted-foreground">
              Free forever for basic PDFs. No account needed — we don&apos;t save anything you type here.
            </p>
          </div>
          
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Form - 2 columns */}
            <div className="lg:col-span-2 space-y-6">
              {/* Sender Details */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Your Details</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="sender-name">Name / Business *</Label>
                      <Input
                        id="sender-name"
                        value={sender.name}
                        onChange={(e) => setSender({...sender, name: e.target.value})}
                        placeholder="Your name or business"
                        data-testid="sender-name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="sender-email">Email</Label>
                      <Input
                        id="sender-email"
                        type="email"
                        value={sender.email}
                        onChange={(e) => setSender({...sender, email: e.target.value})}
                        placeholder="you@example.com"
                      />
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="sender-phone">Phone</Label>
                      <Input
                        id="sender-phone"
                        value={sender.phone}
                        onChange={(e) => setSender({...sender, phone: e.target.value})}
                        placeholder="Phone number"
                      />
                    </div>
                    <div>
                      <Label htmlFor="sender-address">Address</Label>
                      <Input
                        id="sender-address"
                        value={sender.address}
                        onChange={(e) => setSender({...sender, address: e.target.value})}
                        placeholder="Business address"
                      />
                    </div>
                  </div>
                  
                  {/* Logo Upload */}
                  <div>
                    <Label>Logo (optional)</Label>
                    <div className="mt-2">
                      {logoPreview ? (
                        <div className="flex items-center gap-4">
                          <img src={logoPreview} alt="Logo" className="h-16 w-auto object-contain" />
                          <Button variant="ghost" size="sm" onClick={removeLogo}>
                            <X className="w-4 h-4 mr-1" /> Remove
                          </Button>
                        </div>
                      ) : (
                        <label className="flex items-center gap-2 cursor-pointer text-sm text-muted-foreground hover:text-foreground">
                          <ImageIcon className="w-4 h-4" />
                          <span>Upload logo (max 1MB)</span>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleLogoUpload}
                            className="hidden"
                          />
                        </label>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              {/* Client Details */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Client Details</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="client-name">Client Name *</Label>
                      <Input
                        id="client-name"
                        value={client.name}
                        onChange={(e) => setClient({...client, name: e.target.value})}
                        placeholder="Client name"
                        data-testid="client-name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="client-email">Client Email</Label>
                      <Input
                        id="client-email"
                        type="email"
                        value={client.email}
                        onChange={(e) => setClient({...client, email: e.target.value})}
                        placeholder="client@example.com"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="client-address">Client Address</Label>
                    <Input
                      id="client-address"
                      value={client.address}
                      onChange={(e) => setClient({...client, address: e.target.value})}
                      placeholder="Client address"
                    />
                  </div>
                </CardContent>
              </Card>
              
              {/* Invoice Details */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Invoice Details</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid sm:grid-cols-4 gap-4">
                    <div>
                      <Label htmlFor="invoice-number">Invoice #</Label>
                      <Input
                        id="invoice-number"
                        value={invoice.number}
                        onChange={(e) => setInvoice({...invoice, number: e.target.value})}
                        placeholder="INV-001"
                      />
                    </div>
                    <div>
                      <Label htmlFor="invoice-date">Date</Label>
                      <Input
                        id="invoice-date"
                        type="date"
                        value={invoice.date}
                        onChange={(e) => setInvoice({...invoice, date: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label htmlFor="due-date">Due Date</Label>
                      <Input
                        id="due-date"
                        type="date"
                        value={invoice.due_date}
                        onChange={(e) => setInvoice({...invoice, due_date: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label htmlFor="currency">Currency</Label>
                      <Select value={invoice.currency} onValueChange={(v) => setInvoice({...invoice, currency: v})}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {CURRENCIES.map(c => (
                            <SelectItem key={c.code} value={c.code}>{c.symbol} {c.code}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              {/* Line Items */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Line Items</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {items.map((item, index) => (
                    <div key={item.id} className="grid grid-cols-12 gap-2 items-end">
                      <div className="col-span-6">
                        {index === 0 && <Label>Description</Label>}
                        <Input
                          value={item.description}
                          onChange={(e) => updateItem(index, 'description', e.target.value)}
                          placeholder="Service or item description"
                          data-testid={`item-description-${index}`}
                        />
                      </div>
                      <div className="col-span-2">
                        {index === 0 && <Label>Qty</Label>}
                        <Input
                          type="number"
                          min="0"
                          step="0.5"
                          value={item.quantity}
                          onChange={(e) => updateItem(index, 'quantity', e.target.value)}
                        />
                      </div>
                      <div className="col-span-3">
                        {index === 0 && <Label>Rate ({currencySymbol})</Label>}
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={item.rate}
                          onChange={(e) => updateItem(index, 'rate', e.target.value)}
                          data-testid={`item-rate-${index}`}
                        />
                      </div>
                      <div className="col-span-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => removeItem(index)}
                          disabled={items.length === 1}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  <Button variant="outline" size="sm" onClick={addItem} disabled={items.length >= 15}>
                    <Plus className="w-4 h-4 mr-2" /> Add Item
                  </Button>
                </CardContent>
              </Card>
              
              {/* Tax & Notes */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Tax & Notes</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="tax-rate">Tax Rate (%)</Label>
                      <Input
                        id="tax-rate"
                        type="number"
                        min="0"
                        max="100"
                        step="0.1"
                        value={invoice.tax_rate}
                        onChange={(e) => setInvoice({...invoice, tax_rate: parseFloat(e.target.value) || 0})}
                        placeholder="0"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="notes">Notes (optional)</Label>
                    <Textarea
                      id="notes"
                      value={invoice.notes}
                      onChange={(e) => setInvoice({...invoice, notes: e.target.value})}
                      placeholder="Payment instructions, thank you note, etc."
                      rows={3}
                    />
                  </div>
                </CardContent>
              </Card>
            </div>
            
            {/* Summary - 1 column */}
            <div className="lg:col-span-1">
              <div className="sticky top-24">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Summary</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Subtotal</span>
                        <span>{currencySymbol}{subtotal.toFixed(2)}</span>
                      </div>
                      {invoice.tax_rate > 0 && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Tax ({invoice.tax_rate}%)</span>
                          <span>{currencySymbol}{taxAmount.toFixed(2)}</span>
                        </div>
                      )}
                      <div className="flex justify-between font-bold text-lg pt-2 border-t">
                        <span>Total</span>
                        <span data-testid="invoice-total">{currencySymbol}{total.toFixed(2)}</span>
                      </div>
                    </div>
                    
                    <Button 
                      onClick={handleDownload} 
                      disabled={isLoading}
                      className="w-full"
                      size="lg"
                      data-testid="download-btn"
                    >
                      {isLoading ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      ) : (
                        <Download className="w-4 h-4 mr-2" />
                      )}
                      Download PDF Invoice
                    </Button>
                    
                    <p className="text-xs text-muted-foreground text-center">
                      Invoice includes &quot;Made with CreateCollect&quot; footer
                    </p>
                  </CardContent>
                </Card>
                
                {/* Live Preview */}
                <Card className="mt-4">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg">Live Preview</CardTitle>
                      {previewLoading && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
                    </div>
                  </CardHeader>
                  <CardContent>
                    {previewUrl ? (
                      <img
                        src={previewUrl}
                        alt="Invoice preview"
                        className="w-full rounded-md border object-contain"
                        data-testid="free-invoice-preview-image"
                      />
                    ) : (
                      <p className="text-sm text-muted-foreground text-center py-8" data-testid="free-invoice-preview-placeholder">
                        Add your name, client, and an item to see a live preview
                      </p>
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
          
          {/* Niche content: line items */}
          {seoData.lineItems && (
            <div className="mt-16 max-w-3xl mx-auto" data-testid="niche-line-items-section">
              <h2 className="text-2xl font-heading font-semibold mb-6 text-center">What to put on a {seoData.nicheLabel} invoice</h2>
              <div className="space-y-4">
                {seoData.lineItems.map((item, i) => (
                  <div key={i} className="bg-muted/30 rounded-xl p-5">
                    <h3 className="font-semibold mb-1">{item.name}</h3>
                    <p className="text-muted-foreground text-sm">{item.note}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Niche content: payment terms */}
          {seoData.terms && (
            <div className="mt-12 max-w-3xl mx-auto" data-testid="niche-terms-section">
              <h2 className="text-2xl font-heading font-semibold mb-4 text-center">Payment terms that work for {seoData.nicheLabel}</h2>
              <p className="text-muted-foreground leading-relaxed">{seoData.terms}</p>
            </div>
          )}

          {/* FAQs for niche pages */}
          {seoData.faqs && (
            <div className="mt-16 max-w-3xl mx-auto">
              <h2 className="text-2xl font-heading font-semibold mb-6 text-center">Common questions</h2>
              <div className="space-y-6">
                {seoData.faqs.map((faq, i) => (
                  <div key={i} className="bg-muted/30 rounded-xl p-6">
                    <h3 className="font-semibold mb-2">{faq.q}</h3>
                    <p className="text-muted-foreground">{faq.a}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Cross-links */}
          <div className="mt-12 text-center text-sm text-muted-foreground">
            Also for:{' '}
            {NICHE_LINKS.filter(l => l.path !== location.pathname).map((link, i, arr) => (
              <React.Fragment key={link.path}>
                <Link to={link.path} className="text-primary hover:underline">{link.label}</Link>
                {i < arr.length - 1 && ' · '}
              </React.Fragment>
            ))}
          </div>
        </div>
      </main>

      <Footer />
      
      {/* Conversion Modal */}
      {showConversionModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <Card className="max-w-md w-full animate-in zoom-in-95" data-testid="conversion-modal">
            <CardHeader>
              <CardTitle>Nice invoice. Want to get paid faster?</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-muted-foreground">
                With a free CreateCollect account you can email this invoice, let your client pay by card, and track when it&apos;s opened and paid. Free plan includes 2 invoices a month — no card required.
              </p>
              <div className="flex flex-col gap-2">
                <Link to="/register">
                  <Button className="w-full" data-testid="modal-register-btn">Create free account</Button>
                </Link>
                <Button variant="ghost" onClick={() => setShowConversionModal(false)} data-testid="modal-dismiss-btn">
                  No thanks, just the PDF
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default FreeInvoiceGenerator;
